/**
 * Money → Risk flags — prototype VIEWS.flags (docs/prototype.html L4346–4355). GET /money/flags (worst first) with
 * the four tiles from `summary`; each open hit offers Acknowledge, Escalate and Make task (POST
 * /money/flags/{id}/ack|escalate|task); handled hits stay in the list, faded, marked Handled. The "Rules watching"
 * panel switches each of the twelve rules on or off (GET/PATCH /settings/flags, shared with Settings).
 */
import { useState } from 'react';
import { Link } from 'react-router';
import type { FlagHit, FlagKey, Severity } from '@amplispot/contracts';
import { firstName } from '../../lib/format';
import { useFlagAck, useFlagEscalate, useFlagHits, useFlagTask } from '../../lib/money';
import { useSession } from '../../lib/session';
import { Empty, ErrorState, Loading, Pills, PrioMark, errorText, useToast } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';
import { RulesWatching } from '../settings/Thresholds';

const DOT: Record<Severity, string> = { high: 'cold', med: 'warm', low: 'mute' };

/**
 * The row's category chip — prototype FLAGS_STATIC `rule` ('No client contact', 'Revenue vs usage', …): a short
 * category, so the meta line (client · chip · Since) stays on one line. The rule's full sentence (`f.rule`, i.e.
 * FLAG_INFO label) is the chip's tooltip and is what the Rules watching panel lists.
 */
export const FLAG_CHIP: Record<FlagKey, string> = {
  util_below_low: 'Revenue vs usage',
  usage_below_baseline_2w: 'Usage trend',
  no_usage_data_3d: 'Usage data missing',
  no_touchpoint_10d: 'No client contact',
  email_unanswered_3d: 'Email awaiting reply',
  client_task_overdue: 'Client task overdue',
  task_slipped_3x: 'Task slipping',
  blocked_over_3d: 'Blocked',
  request_breach: 'Request breaching',
  checklist_item_missed: 'Checklist item missed',
  revenue_at_risk_top5: 'Revenue at risk',
  held_item_over_4h: 'Held item waiting',
};

const sinceLabel = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

export function RiskFlags() {
  const [sev, setSev] = useState<Severity | 'all'>('all');
  const open = useFlagHits('open', sev);
  const handled = useFlagHits('acknowledged', sev);
  const { can } = useSession();

  const s = open.data?.summary;
  return (
    <>
      <div className="stats">
        <Stat k="Open flags" v={s ? s.open : '–'} />
        <Stat k="High" v={s ? s.high : '–'} cls="red" />
        <Stat k="Medium" v={s ? s.med : '–'} cls="amber" />
        <Stat k="Acknowledged" v={s ? s.acknowledged : '–'} />
      </div>
      <div className="cols c21">
        <div>
          <div className="bar">
            <Pills
              options={[
                ['all', 'All'],
                ['high', 'High'],
                ['med', 'Medium'],
                ['low', 'Low'],
              ]}
              value={sev}
              onChange={setSev}
            />
          </div>
          <div className="panel">
            {open.isPending ? (
              <Loading />
            ) : open.isError ? (
              <ErrorState error={open.error} onRetry={() => open.refetch()} />
            ) : open.data.data.length || handled.data?.data.length || handled.isError ? (
              <>
                {open.data.data.map((f) => (
                  <FlagRow key={f.id} f={f} />
                ))}
                {handled.isError ? (
                  <ErrorState error={handled.error} onRetry={() => handled.refetch()} />
                ) : (
                  (handled.data?.data ?? []).map((f) => <FlagRow key={f.id} f={f} />)
                )}
              </>
            ) : (
              <Empty title="No flags">The rules are watching. Nothing looks wrong right now.</Empty>
            )}
          </div>
        </div>
        {can('settings.write') ? <RulesWatching rail /> : null}
      </div>
    </>
  );
}

function FlagRow({ f }: { f: FlagHit }) {
  const toast = useToast();
  const ack = useFlagAck();
  const esc = useFlagEscalate();
  const task = useFlagTask();
  const busy = ack.isPending || esc.isPending || task.isPending;
  const fail = (e: unknown) => toast(errorText(e));
  const done = f.state !== 'open';
  return (
    <div className="li" style={done ? { opacity: 0.5 } : undefined}>
      <span className={`dot ${DOT[f.severity]}`} style={{ marginTop: 6 }} />
      <div className="b">
        <div className="t">{f.text}</div>
        {/* One compact line, as prototype FLAGS_STATIC: a long client name ellipsises (the ▲ after it never does); chip and date never wrap. */}
        <div className="m flag-meta">
          {f.client ? (
            <span className="who" title={f.client.name}>
              {/* Prototype clientLink(): the name in brand, bold, then ▲ for a high-priority client. */}
              <Link className="nm" to={`/clients/book?client=${f.client.id}`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' }}>
                {f.client.name}
              </Link>
              <PrioMark p={f.client.priority} />
            </span>
          ) : (
            <span className="who" title={f.subject.label}>
              <span className="nm">{f.subject.label}</span>
            </span>
          )}
          <span className="chip" title={f.rule}>
            {FLAG_CHIP[f.flag_key] ?? f.rule}
          </span>
          <span>Since {sinceLabel(f.since)}</span>
          {done && f.handled ? <span>{f.handled.how === 'escalated' ? 'Escalated' : f.handled.how === 'task_made' ? 'Task made' : 'Acknowledged'} by {firstName(f.handled.by.name)}</span> : null}
        </div>
      </div>
      {done ? (
        <span className="chip">Handled</span>
      ) : (
        <>
          <button className="btn sm" disabled={busy} onClick={() => ack.mutate({ id: f.id, body: {} }, { onSuccess: () => toast('Acknowledged'), onError: fail })}>
            Acknowledge
          </button>
          <button
            className="btn sm bad"
            disabled={busy}
            onClick={() =>
              esc.mutate(
                { id: f.id, body: {} },
                { onSuccess: (e) => toast(`Escalation raised${e.owner ? ` for ${firstName(e.owner.name)}` : ''}`), onError: fail },
              )
            }
          >
            Escalate
          </button>
          <button
            className="btn sm pri"
            disabled={busy}
            onClick={() =>
              task.mutate(
                { id: f.id, body: {} },
                { onSuccess: (t) => toast(`Task created for ${t.assignee ? firstName(t.assignee.name) : 'the SPOC'}`), onError: fail },
              )
            }
          >
            Make task
          </button>
        </>
      )}
    </div>
  );
}
